/**
 * Iter XX.265b — Canonical Home-surface state model.
 *
 * SINGLE SOURCE OF TRUTH for whether the authenticated Home hero
 * (`<AuthedHomeHero>`) is currently the top surface. Replaces the
 * fragile `!hasFirstMessage` empty-state gate that could not distinguish
 * system-injected onboarding rows (dream picker, R3 seed, pending-intent
 * optimistic paint) from genuine user activity — and therefore hid the
 * Home from returning users who mounted straight into an onboarding
 * assistant message.
 *
 * Two inputs, one output:
 *
 *   INPUT  messages      : the current chat transcript array.
 *   INPUT  homePinned    : boolean; toggled by the "← Gully" affordance
 *                          in the header when an established user
 *                          explicitly navigates back to Home during an
 *                          active conversation.
 *
 *   OUTPUT hasMeaningfulUserContent : true iff there is at least one
 *                          user-authored row that was NOT system-seeded.
 *                          Historical rows from `/web/history` are
 *                          treated as meaningful by default (they were
 *                          persisted server-side — a real user turn).
 *
 *   OUTPUT isAtHome      : Home hero is the top surface right now.
 *
 * Reachability truth table:
 *
 *   | hasMeaningful | homePinned | isAtHome |
 *   |---------------|------------|----------|
 *   | false         | false      | **true** |
 *   | false         | true       | **true** |
 *   | true          | false      | false    |
 *   | true          | true       | **true** |
 *
 * Design notes:
 *   · System-seeded messages (dream-picker interstitial, R3 seed,
 *     pending-intent optimistic replay) are marked `_system_seeded: true`
 *     at insertion time by Chat.jsx. They still render normally — the
 *     tag only informs Home-gating.
 *   · A `role: "user"` row that survives `send()` (via the dedup path)
 *     has its `_system_seeded` flag CLEARED there, so once the real POST
 *     fires the row is upgraded to meaningful.
 *   · Legacy rows hydrated from `/web/history` never carry the flag —
 *     that is correct: persisted user turns are always meaningful.
 *
 * The metadata is internal only. It never crosses the /api boundary and
 * never surfaces to the user.
 */

/** True iff the row is a system-seeded user bubble. */
export function isSystemSeededUserRow(msg) {
  return !!(
    msg
    && msg.role === "user"
    && msg._system_seeded === true
  );
}

/**
 * Compute the Home-surface state.
 *
 * @param {{messages?: Array, homePinned?: boolean}} args
 * @returns {{hasMeaningfulUserContent: boolean, isAtHome: boolean}}
 */
export function computeHomeSurface({ messages, homePinned } = {}) {
  const list = Array.isArray(messages) ? messages : [];
  const hasMeaningfulUserContent = list.some((m) => (
    m
    && m.role === "user"
    && typeof m.content === "string"
    && m.content.trim().length > 0
    && !isSystemSeededUserRow(m)
  ));
  const isAtHome = !!homePinned || !hasMeaningfulUserContent;
  return { hasMeaningfulUserContent, isAtHome };
}

export default computeHomeSurface;
